import React, { useState } from 'react';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { freeveningApi } from '../services/api';
import { format } from 'date-fns';

const AdminPanel: React.FC = () => {
  const { inviteUser, isAdmin } = useCognitoAuth();
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    is_admin: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // State for clear date functionality
  const [clearDateValue, setClearDateValue] = useState('');
  const [clearDateLoading, setClearDateLoading] = useState(false);
  const [clearDateMessage, setClearDateMessage] = useState('');
  const [clearDateError, setClearDateError] = useState('');

  if (!isAdmin) {
    return (
      <div className="admin-panel">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this area.</p>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await inviteUser(formData);
      setMessage(`Invitation sent to ${formData.email} successfully!`);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        is_admin: false,
      });
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  const handleClearDate = async (e: React.FormEvent) => {
    e.preventDefault();
    setClearDateError('');
    setClearDateMessage('');
    setClearDateLoading(true);

    try {
      const result = await freeveningApi.clearAllFreeveningsForDate(clearDateValue);
      setClearDateMessage(`${result.message}. Removed ${result.removed_count} availability entries.`);
      setClearDateValue('');
    } catch (error: any) {
      setClearDateError(error.response?.data?.error || 'Failed to clear date');
    } finally {
      setClearDateLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      <div className="admin-section">
        <h3>Invite New User</h3>
        
        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="invite-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_admin"
                checked={formData.is_admin}
                onChange={handleChange}
                disabled={loading}
              />
              Grant admin privileges
            </label>
          </div>
          
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Sending Invitation...' : 'Send Invitation'}
          </button>
        </form>
      </div>

      <div className="admin-section">
        <h3>Clear Date Availability</h3>

        {clearDateMessage && <div className="success-message">{clearDateMessage}</div>}
        {clearDateError && <div className="error-message">{clearDateError}</div>}

        <form onSubmit={handleClearDate} className="clear-date-form">
          <div className="form-group">
            <label htmlFor="clearDate">Select Date to Clear</label>
            <input
              type="date"
              id="clearDate"
              value={clearDateValue}
              onChange={(e) => setClearDateValue(e.target.value)}
              required
              disabled={clearDateLoading}
              min={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>

          <button type="submit" disabled={clearDateLoading || !clearDateValue} className="submit-button danger">
            {clearDateLoading ? 'Clearing...' : 'Clear All Availability for This Date'}
          </button>
        </form>

        <div className="warning-note">
          <strong>⚠️ Warning:</strong> This will remove ALL user availability entries for the selected date. This action cannot be undone.
        </div>
      </div>

      <div className="admin-info">
        <h3>How Invitations Work</h3>
        <ul>
          <li>User receives an email with temporary credentials</li>
          <li>They must sign in and set a permanent password</li>
          <li>Account expires in 7 days if not activated</li>
          <li>Only invited users can access the application</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPanel;